/**
 * @brisa/ui · facelift/pass-card/PassCardWithoutCta
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #12 (card-plan-without-cta)
 * Figma origem: node 163:934 — reforma per 179:275 (Bruno 2026-05-14):
 *   - bg rgba(0,0,0,0.08) (--text-08, NÃO dark scrim)
 *   - rounded-[32px] (xl), w-248, padding p-24
 *   - SEM TÍTULO — pill SectionTag em cima, descrição abaixo
 *   - description text-[hsl(var(--text-48))]
 *   - info rows text-black com border-bottom rgba(0,0,0,0.08)
 *
 * Sem 'use client' — apresentacional puro.
 */
import * as React from 'react';

import { cn } from '@brisa/ui/lib/utils';

export interface PassCatalogItem {
  key: string;
  name: string;
  description?: string;
  price_eur_per_period: number;
  period?: 'week' | 'month' | 'year' | string;
  is_recurring?: boolean;
  visits?: number;
  events?: number;
}

export interface PassCardWithoutCtaProps extends React.HTMLAttributes<HTMLDivElement> {
  plan: PassCatalogItem;
  /** label da pill superior; default deriva do `plan.key`. */
  tag?: string;
  /** Substitui a renderização do preço calculado (ex: "em breve" pré-MVP). */
  priceOverride?: string;
}

const PERIOD_LABEL: Record<string, string> = {
  week: '07 dias',
  month: 'mensal',
  year: 'anual',
};

const DEFAULT_TAG: Record<string, string> = {
  visitor_week: 'DE PASSAGEM',
  regular_month: 'SOCIAL',
  basic_month: 'BASIC PASS',
  premium_month: 'PREMIUM PASS',
};

function formatPrice(plan: PassCatalogItem): string {
  const padded = String(plan.price_eur_per_period).padStart(2, '0');
  const value = `€${padded}`;
  if (plan.is_recurring) return `${value}/mês`;
  return value;
}

export function PassCardWithoutCta({
  plan,
  tag,
  priceOverride,
  className,
  children,
  ...props
}: PassCardWithoutCtaProps) {
  const tagText = tag ?? DEFAULT_TAG[plan.key] ?? plan.name?.toUpperCase() ?? 'PASS';
  const periodLabel = PERIOD_LABEL[plan.period ?? 'month'] ?? plan.period ?? '';

  return (
    <article
      className={cn(
        'flex w-full flex-col justify-between gap-6 rounded-[var(--radius-xl)] bg-[hsl(var(--text-08))] p-6',
        'min-h-[284px]',
        className,
      )}
      data-slot="pass-card"
      {...props}
    >
      {/* Description block — tag pill + parágrafo (SEM título per PL 2026-05-14) */}
      <div className="flex flex-col items-start gap-3" data-slot="description">
        <span
          data-slot="tag"
          className="inline-flex items-center justify-center rounded-full bg-[hsl(var(--text-08))] px-3 py-2 text-xs font-semibold text-[hsl(var(--text-64))]"
        >
          {tagText}
        </span>
        {plan.description ? (
          <p
            data-slot="body"
            className="text-sm leading-tight text-[hsl(var(--text-48))]"
          >
            {plan.description}
          </p>
        ) : null}
      </div>

      {/* Information block — 3 rows com border-bottom separator */}
      <dl className="flex w-full flex-col gap-2 text-sm text-[hsl(var(--text-100))]" data-slot="information">
        <div
          className="flex items-center justify-between border-b border-[hsl(var(--text-08))] pb-2 font-semibold"
          data-slot="stat"
        >
          <dt>Período</dt>
          <dd>{periodLabel}</dd>
        </div>
        <div
          className="flex items-center justify-between border-b border-[hsl(var(--text-08))] pb-2 font-semibold"
          data-slot="stat"
        >
          <dt>Clubs</dt>
          <dd>{plan.visits != null ? String(plan.visits).padStart(2, '0') : '—'}</dd>
        </div>
        <div
          className="flex items-center justify-between border-b border-[hsl(var(--text-08))] pb-2"
          data-slot="stat"
        >
          <dt className="font-semibold">Valor</dt>
          <dd className="text-lg font-bold">{priceOverride ?? formatPrice(plan)}</dd>
        </div>
      </dl>

      {children ? <div data-slot="cta-slot">{children}</div> : null}
    </article>
  );
}
